/**
 * Session Detail View
 *
 * Shows detailed results for a single quiz session:
 * - Map with features colored by result (correct/incorrect/not asked)
 * - Session summary with encouraging language
 * - Question-by-question breakdown
 *
 * Philosophy: Frame mistakes as "areas to review", celebrate progress.
 * See: /docs/STATS_PHILOSOPHY.md
 */

import { useMemo, useState, useEffect } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db/database";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, XCircle, X } from "lucide-react";
import { QuizSession, QuizConfig } from "@/db/types";
import { getDifficultyBadgeLabel, getDifficultyBadgeVariant } from "@/utils/difficultyCalculator";
import { getQuizFlag } from "@/utils/quizMetadata";
import { SessionResultMap } from "./SessionResultMap";
import { fetchQuizRunFromSupabase, type QuizRunData } from "@/lib/quizRunSync";

// Helper to identify anomalous question times (likely user went AFK)
const MAX_QUESTION_TIME_MS = 5 * 60 * 1000; // 5 minutes
function isAnomalousQuestionTime(timeMs: number): boolean {
  return timeMs > MAX_QUESTION_TIME_MS;
}

interface SessionDetailViewProps {
  session: QuizSession;
  config: QuizConfig;
  isOpen: boolean;
  onClose: () => void;
  /** Optional: Previous session to show improvement */
  previousSession?: QuizSession;
}

export function SessionDetailView({
  session,
  config,
  isOpen,
  onClose,
  previousSession,
}: SessionDetailViewProps) {
  // Try to fetch detailed data from Supabase (for logged-in users)
  const [supabaseData, setSupabaseData] = useState<QuizRunData | null>(null);
  const [loadingSupabase, setLoadingSupabase] = useState(true);
  const [supabaseFetchFailed, setSupabaseFetchFailed] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    async function loadSupabaseData() {
      try {
        setLoadingSupabase(true);
        setSupabaseFetchFailed(false);
        const data = await fetchQuizRunFromSupabase(session.id);
        if (data) {
          setSupabaseData(data);
        } else {
          console.log('[SessionDetail] No Supabase data found for session:', session.id);
          setSupabaseFetchFailed(true);
        }
      } catch (error) {
        console.error("[SessionDetail] Failed to load Supabase data:", error);
        setSupabaseData(null);
        setSupabaseFetchFailed(true);
      } finally {
        setLoadingSupabase(false);
      }
    }

    loadSupabaseData();
  }, [session.id, isOpen]);

  // Load basic attempts for fallback (when Supabase data not available)
  const localAttempts = useLiveQuery(
    () => db.quiz_attempts.where("sessionId").equals(session.id).toArray(),
    [session.id]
  );

  // Use Supabase data if available, otherwise fall back to local
  const attempts = supabaseData?.attempts || localAttempts || [];
  const hasDetailedData = !!supabaseData;

  // Calculate stats
  const stats = useMemo(() => {
    if (!attempts || attempts.length === 0) return null;

    const correctAttempts = attempts.filter((a: any) =>
      a.wasCorrect ?? (a.final_result === 'correct')
    );
    const incorrectAttempts = attempts.filter((a: any) =>
      !(a.wasCorrect ?? (a.final_result === 'correct'))
    );

    // Calculate improvement
    const improvement = previousSession
      ? session.accuracyRate - previousSession.accuracyRate
      : null;

    // Get difficulty info
    const difficultyMultiplier = session.difficultyMultiplier || 1.0;
    const difficultyLabel = getDifficultyBadgeLabel(difficultyMultiplier);
    const difficultyVariant = getDifficultyBadgeVariant(difficultyMultiplier);

    // Get active map settings
    const mapSettings: string[] = [];
    if (session.displaySettings) {
      if (session.displaySettings.hideOutlines) mapSettings.push("No Borders");
      if (session.displaySettings.disableHover) mapSettings.push("No Hover");
      if (session.displaySettings.hideIndicatorColors) mapSettings.push("No Feedback");
      if (session.displaySettings.hideLabels) mapSettings.push("No Labels");
    }

    return {
      correctAttempts,
      incorrectAttempts,
      improvement,
      difficultyLabel,
      difficultyVariant,
      mapSettings,
    };
  }, [attempts, session, previousSession]);

  // Show loading only while Supabase is being fetched
  if (loadingSupabase) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Loading session details...</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center text-muted-foreground">
            Fetching detailed results...
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If no data available at all, show a message
  if (!attempts.length && !supabaseData) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getQuizFlag(config)}</span>
                <div>
                  <div className="text-lg font-semibold">{config.title}</div>
                  <div className="text-sm text-muted-foreground font-normal">
                    {new Date(session.completedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    •{" "}
                    {new Date(session.completedAt).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="p-6">
            <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">ℹ️</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Limited Data Available</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      This quiz was completed on another device, and detailed question data is not available.
                      {supabaseFetchFailed && " Cloud sync may not have completed on the original device."}
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="font-semibold text-foreground">
                          {session.correctAnswers} of {session.totalQuestions}
                        </span>{" "}
                        correct ({session.accuracyRate.toFixed(0)}% accuracy)
                      </p>
                      <p>
                        <span className="font-semibold text-foreground">
                          {formatTime(session.totalTimeMs)}
                        </span>{" "}
                        total time
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isPerfectScore = session.accuracyRate === 100;
  const isGoodScore = session.accuracyRate >= 80;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{getQuizFlag(config)}</span>
              <div>
                <div className="text-lg font-semibold">{config.title}</div>
                <div className="text-sm text-muted-foreground font-normal">
                  {new Date(session.completedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  •{" "}
                  {new Date(session.completedAt).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-8rem)] pr-4">
          <div className="space-y-6">
            {/* Encouraging Summary Card */}
            <Card
              className={
                isPerfectScore
                  ? "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950"
                  : isGoodScore
                  ? "border-primary/30 bg-primary/5"
                  : "border-border"
              }
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-5xl">
                    {isPerfectScore ? "🎯" : isGoodScore ? "✨" : "💪"}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">
                      {isPerfectScore
                        ? "Perfect Score! 🎉"
                        : isGoodScore
                        ? "Great Job!"
                        : "Good Practice Session!"}
                    </h3>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p>
                        <span className="font-semibold text-foreground">
                          {session.correctAnswers} of {session.totalQuestions}
                        </span>{" "}
                        correct ({session.accuracyRate.toFixed(0)}% accuracy)
                      </p>
                      {stats && stats.incorrectAttempts.length > 0 && (
                        <p>
                          {stats.incorrectAttempts.length}{" "}
                          {stats.incorrectAttempts.length === 1
                            ? "area identified"
                            : "areas identified"}{" "}
                          for review
                        </p>
                      )}
                      {stats?.improvement !== null && stats?.improvement !== undefined && stats.improvement > 0 && (
                        <p className="text-green-600 dark:text-green-400 font-medium">
                          ↑ {stats.improvement.toFixed(0)}% improvement from previous
                          session!
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="text-right text-sm">
                    <div className="font-semibold">{formatTime(session.totalTimeMs)}</div>
                    <div className="text-xs text-muted-foreground">Total time</div>
                    {stats?.difficultyLabel && (
                      <Badge variant={stats.difficultyVariant} className="mt-2">
                        {stats.difficultyLabel}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map Settings Used */}
            {stats?.mapSettings && stats.mapSettings.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Challenge settings:</span>
                {stats.mapSettings.map((setting) => (
                  <Badge key={setting} variant="outline" className="text-xs">
                    {setting}
                  </Badge>
                ))}
              </div>
            )}

            {/* Results Map */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Visual Results</CardTitle>
                <p className="text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 bg-green-500 rounded-sm"></span>
                    Correct
                  </span>
                  {" • "}
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 bg-orange-500 rounded-sm"></span>
                    To review
                  </span>
                  {" • "}
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 bg-gray-300 rounded-sm"></span>
                    Not asked
                  </span>
                </p>
              </CardHeader>
              <CardContent>
                <SessionResultMap
                  config={config}
                  attempts={attempts as any}
                  height="400px"
                />
              </CardContent>
            </Card>

            {/* Question Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Question Breakdown
                  {!hasDetailedData && (
                    <Badge variant="outline" className="ml-2 text-xs font-normal">
                      Basic View
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  All {attempts.length} questions in this session
                  {!hasDetailedData && " • Detailed answers available for logged-in users"}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {attempts
                    .sort((a: any, b: any) => {
                      const aTime = new Date(a.attemptedAt || a.attempted_at).getTime();
                      const bTime = new Date(b.attemptedAt || b.attempted_at).getTime();
                      return aTime - bTime;
                    })
                    .map((attempt: any, index: number) => {
                      const timeSpent = attempt.timeSpentMs || attempt.time_spent_ms || 0;
                      const isCorrect = attempt.wasCorrect ?? (attempt.final_result === 'correct');
                      const attemptsCount = attempt.attemptsUsed || attempt.attempts_used || 1;
                      const userAttempts = attempt.user_attempts || [];

                      return (
                        <div
                          key={attempt.id}
                          className={`p-4 rounded-lg border ${
                            isCorrect
                              ? "border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20"
                              : "border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div className="flex-shrink-0 mt-0.5">
                              {isCorrect ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-muted-foreground">
                                  Q{index + 1}
                                </span>
                                <span className="text-sm font-medium">
                                  {attempt.promptText || attempt.prompt_text}
                                </span>
                              </div>

                              <div className="space-y-1 text-sm">
                                {/* Show detailed answers if available */}
                                {hasDetailedData && userAttempts.length > 0 ? (
                                  <>
                                    <div>
                                      <span className="text-muted-foreground">Correct answer: </span>
                                      <span className="font-medium text-green-600 dark:text-green-400">
                                        {attempt.correct_answer}
                                      </span>
                                    </div>
                                    {!isCorrect && (
                                      <div className="mt-2">
                                        <span className="text-muted-foreground text-xs">Your attempts:</span>
                                        <div className="ml-2 mt-1 space-y-0.5">
                                          {userAttempts.map((ua: any, i: number) => (
                                            <div key={i} className="text-xs">
                                              <span className="text-orange-600 dark:text-orange-400">
                                                {i + 1}. {ua.answer}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  /* Basic view - show what they clicked vs correct answer */
                                  <div className="space-y-1">
                                    <div>
                                      <span className="text-muted-foreground">Correct: </span>
                                      <span className="font-medium text-green-600 dark:text-green-400">
                                        {attempt.featureLabel || attempt.feature_label}
                                      </span>
                                    </div>
                                    {!isCorrect && (attempt.clickedFeatureLabel || attempt.clicked_feature_label) && (
                                      <div>
                                        <span className="text-muted-foreground">You clicked: </span>
                                        <span className="text-orange-600 dark:text-orange-400">
                                          {attempt.clickedFeatureLabel || attempt.clicked_feature_label}
                                        </span>
                                      </div>
                                    )}
                                    {attemptsCount > 1 && (
                                      <div className="text-xs text-muted-foreground">
                                        ({attemptsCount} {attemptsCount === 1 ? "attempt" : "attempts"})
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Time */}
                            <div className="flex-shrink-0 text-right text-xs">
                              <div className={isAnomalousQuestionTime(timeSpent) ? "text-muted-foreground line-through opacity-60" : "text-muted-foreground"}>
                                {formatTime(timeSpent)}
                              </div>
                              {isAnomalousQuestionTime(timeSpent) && (
                                <div className="text-[10px] text-orange-600 dark:text-orange-400 mt-0.5" title="Time excluded from aggregates (>5 minutes)">
                                  Excluded
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Encouraging CTA (for sessions with incorrect answers) */}
            {stats && stats.incorrectAttempts.length > 0 && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    💡 Remember: Mistakes help you learn! Review these{" "}
                    {stats.incorrectAttempts.length}{" "}
                    {stats.incorrectAttempts.length === 1 ? "area" : "areas"} and
                    you'll improve even more.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => {
                      window.location.href = `/quiz/play?signature=${config.signature}`;
                    }}
                  >
                    Practice Again
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Utilities
// ============================================================================

function formatTime(ms: number): string {
  const minutes = Math.floor(ms / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}
